import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import useTranslate from '../../../Hooks/useTranslate';
import RegionWidget from './RegionWidget';
import { useDetailData } from './DetailData';
import { widgetHasContent } from './WidgetRegistry';

/**
 * A region's children as tabs, one shown at a time.
 *
 * The container the detail page has always had, declared rather than assembled: each child is a
 * region like any other, so a tab may hold fields today and something else tomorrow without this
 * knowing.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.region - The compiled region, carrying its child regions.
 *
 * @returns {React.JSX.Element}
 */
const TabsWidget = React.memo(function TabsWidget({ region }) {
  const translate = useTranslate();
  // While the tabs are being arranged, the tab being configured is the one shown: a change made to
  // a tab that is not on screen is a change made blind.
  const page = useDetailData();
  const { chosen, editing } = page;
  // A tab with nothing behind it is left out, as the box these replaced always did. While the
  // layout is being arranged every tab stays, or one could not be configured.
  const regions = React.useMemo(
    () =>
      (region?.regions ?? []).filter(
        (child) => editing || widgetHasContent(child, page)
      ),
    [region, editing, page]
  );
  const [openId, setOpenId] = useState(regions[0]?.id ?? '');

  // A layout edited under the editor can drop the tab in view, so the first one takes over.
  useEffect(() => {
    if (regions.length > 0 && !regions.some(({ id }) => id === openId)) {
      setOpenId(regions[0].id);
    }
  }, [regions, openId]);

  // `chosen` is the whole way down to what is being configured, so a tab holding the container
  // holding it opens too: at any depth, the thing being arranged is on screen.
  const onPath = regions.find(({ id }) => (chosen ?? []).includes(id));
  const open =
    onPath ?? regions.find(({ id }) => id === openId) ?? regions[0] ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* One tab needs no bar on a page - there is nothing to switch to - but while the container is
          being arranged the bar is what says it is one, and what reaches the tab being built. A
          container drawn as its only child looks like a plain region with no way to add a second. */}
      {(editing || regions.length > 1) && (
        <Tabs
          value={open?.id ?? ''}
          onChange={(_, chosen) => setOpenId(chosen)}
          variant="fullWidth"
          sx={{ minHeight: '36px', flex: 'none' }}
        >
          {regions.map((child) => (
            <Tab
              key={child.id}
              value={child.id}
              label={translate(child.label ?? child.id)}
            />
          ))}
        </Tabs>
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {open && <RegionWidget region={open} />}
      </Box>
    </Box>
  );
});

export default TabsWidget;
